import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { merge } from 'lodash';
import { ThemeProvider as NivoThemeProvider, defaultTheme as defaultNivoTheme, PartialTheme } from '@nivo/theming';
import { ModeCategory } from '@commutifi/modes';
import { Themes } from '@commutifi-fe/ui/components/theme/constants';
import { ChartsThemeProviderProps } from './props';
import { extractThemeFromDocumentStyle } from '../theme';
import { ChartsThemeContext, ChartsThemeContextValue, defaultConfig } from './context';
import { colors } from '../theme/colors';
import defaultTheme from '../theme/themes/default.module.scss';
import darkTheme from '../theme/themes/dark.module.scss';
const fontFamily = 'Segma, sans-serif';
export const defaultNivoThemeOverrides: PartialTheme = {
  text: {
    fill: colors.neutralGray600,
    fontFamily
  },
  axis: {
    legend: {
      text: {
        fill: colors.neutralGray500
      }
    },
    ticks: {
      text: {
        fill: colors.neutralGray600,
        fontSize: '10px',
        fontFamily
      },
      line: {
        stroke: colors.neutralGray200
      }
    },
    domain: {
      line: {
        padding: 10
      }
    }
  },
  crosshair: {
    line: {
      stroke: colors.neutralGray400
    }
  },
  grid: {
    line: {
      stroke: colors.neutralGray200
    }
  },
  legends: {
    text: {
      fill: colors.neutralGray600,
      fontFamily,
      fontWeight: 600
    },
    title: {
      text: {
        fill: colors.neutralGray600,
        fontFamily
      }
    },
    ticks: {
      text: {
        fill: colors.neutralGray600,
        fontFamily
      },
      line: {
        stroke: colors.neutralGray200
      }
    }
  },
  annotations: {
    text: {
      fill: colors.neutralGray600,
      fontFamily
    },
    link: {
      stroke: colors.neutralBlue500
    }
  },
  tooltip: {
    container: {
      color: colors.neutralGray600
    }
  }
};
export function ChartsThemeProvider(t0) {
  const $ = _c(16);
  const {
    children,
    theme,
    colorScheme
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = extractThemeFromDocumentStyle();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [dynamicColors, setDynamicColors] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = newTheme => {
      bb2: switch (newTheme) {
        case "dark":
        case Themes.DARK:
          {
            Object.entries(darkTheme).forEach(_temp);
            break bb2;
          }
        case "light":
        case Themes.LIGHT:
        default:
          {
            Object.entries(defaultTheme).forEach(_temp2);
          }
      }
      setDynamicColors(extractThemeFromDocumentStyle());
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const changeTheme = t2;
  let t3;
  let t4;
  if ($[2] !== colorScheme) {
    t3 = () => {
      changeTheme(colorScheme || Themes.LIGHT);
    };
    t4 = [changeTheme, colorScheme];
    $[2] = colorScheme;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] !== theme) {
    t5 = merge({}, defaultNivoThemeOverrides, theme);
    $[5] = theme;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const memoedTheme = t5;
  let t6;
  if ($[7] !== dynamicColors || $[8] !== memoedTheme) {
    t6 = merge({}, defaultConfig, {
      tokens: dynamicColors,
      theme: merge({}, defaultNivoTheme, memoedTheme),
      customPalettes: {
        blue: [dynamicColors.chartNeutralBlue500, dynamicColors.chartNeutralBlue200, dynamicColors.chartNeutralBlue600, dynamicColors.chartNeutralBlue100, dynamicColors.chartNeutralBlue400, dynamicColors.chartNeutralBlue300],
        blueDescending: [dynamicColors.chartNeutralBlue600, dynamicColors.chartNeutralBlue500, dynamicColors.chartNeutralBlue400, dynamicColors.chartNeutralBlue300, dynamicColors.chartNeutralBlue200, dynamicColors.chartNeutralBlue100],
        perModeCategory: {
          [ModeCategory.Carpool]: [dynamicColors.chartAccentYellow200, dynamicColors.chartAccentYellow600, dynamicColors.chartAccentYellow500, dynamicColors.chartAccentYellow400, dynamicColors.chartAccentYellow300, dynamicColors.chartAccentYellow100],
          [ModeCategory.SharedRide]: [dynamicColors.chartAccentBeige400, dynamicColors.chartAccentBeige600, dynamicColors.chartAccentBeige500, dynamicColors.chartAccentBeige300, dynamicColors.chartAccentBeige200, dynamicColors.chartAccentBeige100],
          [ModeCategory.Bike]: [dynamicColors.chartAccentMint500, dynamicColors.chartAccentMint600, dynamicColors.chartAccentMint400, dynamicColors.chartAccentMint300, dynamicColors.chartAccentMint200, dynamicColors.chartAccentMint100],
          [ModeCategory.PublicTransit]: [dynamicColors.chartAccentGreen400, dynamicColors.chartAccentGreen600, dynamicColors.chartAccentGreen500, dynamicColors.chartAccentGreen300, dynamicColors.chartAccentGreen200, dynamicColors.chartAccentGreen100],
          [ModeCategory.Walk]: [dynamicColors.chartAccentPistachio300, dynamicColors.chartAccentPistachio600, dynamicColors.chartAccentPistachio500, dynamicColors.chartAccentPistachio400, dynamicColors.chartAccentPistachio200, dynamicColors.chartAccentPistachio100],
          [ModeCategory.Drive]: [dynamicColors.chartAccentPurple500, dynamicColors.chartAccentPurple600, dynamicColors.chartAccentPurple400, dynamicColors.chartAccentPurple300, dynamicColors.chartAccentPurple200, dynamicColors.chartAccentPurple100],
          [ModeCategory.Remote]: [dynamicColors.chartAccentPink400, dynamicColors.chartAccentPink600, dynamicColors.chartAccentPink500, dynamicColors.chartAccentPink300, dynamicColors.chartAccentPink200, dynamicColors.chartAccentPink100],
          [ModeCategory.None]: [dynamicColors.chartNeutralBlue500, dynamicColors.chartNeutralBlue200, dynamicColors.chartNeutralBlue600, dynamicColors.chartNeutralBlue100, dynamicColors.chartNeutralBlue400, dynamicColors.chartNeutralBlue300]
        }
      }
    });
    $[7] = dynamicColors;
    $[8] = memoedTheme;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const memoedConfig = t6;
  let t7;
  if ($[10] !== children || $[11] !== memoedConfig) {
    t7 = <ChartsThemeContext value={memoedConfig}>{children}</ChartsThemeContext>;
    $[10] = children;
    $[11] = memoedConfig;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== memoedTheme || $[14] !== t7) {
    t8 = <NivoThemeProvider theme={memoedTheme}>{t7}</NivoThemeProvider>;
    $[13] = memoedTheme;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}
function _temp2(t0) {
  const [key, value] = t0;
  document.documentElement.style.setProperty(key, value);
}
function _temp(t0) {
  const [key_0, value_0] = t0;
  document.documentElement.style.setProperty(key_0, value_0);
}
export default ChartsThemeProvider;